import getObjectKey from 'lib/object/getObjectKey'
import { getJourneyV2AveragePricePerTraveller } from 'lib/flights/flightUtils'
import { getAllOrderItems, isItemCancelled } from 'lib/order/orderUtils'
import { EmptyArray, min, nonNullable } from 'lib/array/arrayUtils'
import { ISO_DATE_REGEX, addDays, beginningOfToday, formatIsoDate, isValidDate } from 'lib/datetime/dateUtils'
import { DMY_DATETIME_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'

/**
 * Utility functions for flight date change flow
 */

export const getFlightDateChangeUpsellPricePerLeg = (
  departingJourney: App.JourneyV2,
  returningJourney: App.JourneyV2 | undefined,
  selectedLegCount: number,
): number => {
  const totalJourneyPricePerTraveller = getJourneyV2AveragePricePerTraveller(departingJourney) +
    (returningJourney ? getJourneyV2AveragePricePerTraveller(returningJourney) : 0)

  return totalJourneyPricePerTraveller / selectedLegCount
}

export const getFlightDateChangeSearchKey = (
  reservationId: string,
  selectedLegs: Array<App.LegWithFlightItemIdWithNewDepartureDate>,
): string => getObjectKey({
  reservationId,
  selectedLegs: selectedLegs.map(leg => leg.id).join(','),
  dates: selectedLegs.map(leg => leg.newDepartureDate).join(','),
})

/**
 * Creates a combined fare key for upsell lookup
 * The key format is 'journeyId1,journeyId2,...' matching all legs in order
 */
export const createCombinedFareKey = (
  selectedJourneys: Record<string, App.JourneyV2>,
  allLegs: Array<App.LegWithFlightItemId>,
): string => {
  return allLegs
    .map(leg => selectedJourneys[leg.id]?.id)
    .filter(Boolean)
    .join(',')
}

/**
 * Gets the complete journey selection including user selections and auto-selected unchanged legs
 * For unchanged legs, respects pairedFareId relationships using bookingInfo.boookingKey
 */
export const getCompleteJourneySelection = (
  userSelectedJourneys: Record<string, App.JourneyV2>,
  allAvailableJourneys: Array<Array<App.JourneyV2>>,
  selectedLegs: Array<App.LegWithFlightItemIdWithNewDepartureDate>,
  allLegs: Array<App.LegWithFlightItemId>,
): Record<string, App.JourneyV2> => {
  const result: Record<string, App.JourneyV2> = { ...userSelectedJourneys }

  // Build a map of journeys by bookingKey for each leg (used for pairing)
  const journeysByBookingKeyPerLeg = allAvailableJourneys.map(journeys => {
    const map = new Map<string, App.JourneyV2>()
    journeys.forEach(journey => {
      if (journey.bookingInfo?.boookingKey) {
        map.set(journey.bookingInfo.boookingKey, journey)
      }
    })
    return map
  })

  // Auto-select journeys for unchanged legs, respecting pairedFareId
  allLegs.forEach((leg, legIndex) => {
    const isSelectedForChange = selectedLegs.some(selectedLeg => selectedLeg.id === leg.id)

    // If this leg is not being changed and not already selected
    if (!isSelectedForChange && !result[leg.id] && allAvailableJourneys[legIndex]?.length > 0) {
      let foundPairedJourney: App.JourneyV2 | undefined

      // Check all user-selected journeys for pairing information
      for (const selectedJourney of Object.values(userSelectedJourneys)) {
        if (selectedJourney.pairedFareId?.length) {
          // Find a journey in this leg whose bookingKey matches the paired list
          for (const pairedBookingKey of selectedJourney.pairedFareId) {
            const pairedJourney = journeysByBookingKeyPerLeg[legIndex].get(pairedBookingKey)
            if (pairedJourney) {
              foundPairedJourney = pairedJourney
              break
            }
          }
        }
        if (foundPairedJourney) break
      }

      // Use paired journey if found, otherwise fall back to first available
      result[leg.id] = foundPairedJourney || allAvailableJourneys[legIndex][0]
    }
  })

  return result
}

/**
 * Determines the current page location in the flight date change flow based on URL pathname.
 * Used for reliable navigation since Redux state may not always match the actual page.
 */
export function getFlightChangeDatesPageLocation(pathname: string) {
  return {
    isOnFlightChangePage: pathname.includes('flight-change-select-flight-to-change'),
    isOnInsurancePage: pathname.includes('insurance-select-dates'),
    isOnPurchasePage: pathname.includes('/purchase'),
  }
}

// ISO_DATE_REGEX only matches a prefix, and `2026-02-30` parses but rolls over to March 2.
function parseIsoDateParam(value: string): string | undefined {
  if (!ISO_DATE_REGEX.test(value)) return undefined
  const parsed = new Date(`${value}T00:00:00`)
  return isValidDate(parsed) && formatIsoDate(parsed) === value ? value : undefined
}

/** One leg a deeplink asks to move, before it has been matched against the booking. */
export interface FlightDateChangeDeeplinkLeg {
  legId: string
  newDepartureDate: string
  /** Empty when the link named the leg but not which flight to put it on. */
  flightNumbers: Array<string>
  cabin?: string
  fare?: string
}

/**
 * Days of notice flights search needs — it returns nothing for today or tomorrow, which is why the
 * date-change calendar disables both.
 */
export const FLIGHT_CHANGE_SEARCH_LEAD_DAYS = 2

/** The earliest date flights search will return an exchange for. */
export function getEarliestFlightChangeDepartureDate(): string {
  return formatIsoDate(addDays(beginningOfToday(), FLIGHT_CHANGE_SEARCH_LEAD_DAYS))
}

/** The dates before {@link getEarliestFlightChangeDepartureDate}, for the calendar to disable. */
export function getFlightChangeUnavailableDepartureDates(): Array<string> {
  const today = beginningOfToday()
  return Array.from({ length: FLIGHT_CHANGE_SEARCH_LEAD_DAYS }, (_unused, offset) =>
    formatIsoDate(addDays(today, offset)),
  )
}

/** The query param a deeplink carries the legs it wants moved in. */
export const FLIGHT_DATE_CHANGE_LEGS_PARAM = 'legs'

/**
 * Reads the legs a deeplink asks to move.
 *
 * One `legs` param carries every leg as comma-separated
 * `<legId>:<newDepartureDate>[:<flightNumbers>[:<cabin>[:<fare>]]]` entries, so
 * `?legs=leg-1:2026-09-29:GA715,leg-2:2026-10-10:GA409|GA712` moves both halves of a return trip and
 * nominates the flight for each. A leg's own segments are `|`-separated, since `,` already separates legs
 * and `:` their fields. Leave the third field off and the customer picks that leg from the list.
 *
 * Cabin and fare are what the agent quoted a price against — `:Economy:Flex` — and are matched in that
 * order, since a cabin that has sold out takes its fare options with it.
 *
 * Deliberately one param rather than repeated `legId`/`newDepartureDate`/`flightNumbers` keys: these links
 * reach customers through SMS and chat, where duplicate query keys get collapsed, and a collapsed set
 * would silently move one leg — or nominate a flight nobody agreed to — instead of failing. Holding every
 * leg in one value makes the pairing structural, so nothing can slip a position.
 *
 * Returns undefined for a link that cannot be trusted — a malformed entry, a repeated leg, a date that is
 * not a real one, or a date flights search will not return an exchange for — rather than acting on the
 * part of it that happened to parse. `earliestDepartureDate` is passed in rather than read from the clock
 * so this stays pure.
 */
export function parseFlightDateChangeDeeplinkLegs(
  query: URLSearchParams,
  earliestDepartureDate: string,
): Array<FlightDateChangeDeeplinkLeg> | undefined {
  const entries = (query.get(FLIGHT_DATE_CHANGE_LEGS_PARAM) ?? '')
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean)

  if (!entries.length) return undefined

  const requested: Array<FlightDateChangeDeeplinkLeg> = []

  for (const entry of entries) {
    const [legId, date, flightNumbers, cabin, fare, ...unexpected] = entry.split(':').map(part => part.trim())
    if (!legId || !date || unexpected.length) return undefined

    const newDepartureDate = parseIsoDateParam(date)
    if (!newDepartureDate) return undefined
    // ISO dates compare correctly as strings.
    if (newDepartureDate < earliestDepartureDate) return undefined

    requested.push({
      legId,
      newDepartureDate,
      flightNumbers: (flightNumbers ?? '')
        .split('|')
        .map(value => value.trim())
        .filter(Boolean),
      cabin: cabin || undefined,
      fare: fare || undefined,
    })
  }

  if (new Set(requested.map(leg => leg.legId)).size !== requested.length) return undefined

  return requested
}

/**
 * The flights a deeplink nominated, keyed by the leg each one belongs to. Legs the link named without a
 * flight are left out, so the customer picks those from the list themselves.
 */
export function buildPreselectedFlightsByLeg(
  requested: Array<FlightDateChangeDeeplinkLeg>,
): Record<string, App.PreselectedFlight> | undefined {
  const byLeg: Record<string, App.PreselectedFlight> = {}

  for (const { legId, newDepartureDate, flightNumbers, cabin, fare } of requested) {
    if (flightNumbers.length) byLeg[legId] = { flightNumbers, newDepartureDate, cabin, fare }
  }

  return Object.keys(byLeg).length ? byLeg : undefined
}

/** Drops one leg's nomination, collapsing to undefined once none are left. */
export function dropPreselectedFlight(
  byLeg: Record<string, App.PreselectedFlight>,
  legId: string,
): Record<string, App.PreselectedFlight> | undefined {
  const remaining = Object.fromEntries(Object.entries(byLeg).filter(([id]) => id !== legId))
  return Object.keys(remaining).length ? remaining : undefined
}

/**
 * Matches the legs a deeplink named against the booking, in booking order.
 *
 * The order is load-bearing rather than cosmetic: the flight list works through the legs in sequence and
 * filters each one's fares by the previous leg's selection (`pairedFareId`), so a link that named the
 * return leg first would break that chain.
 *
 * Undefined if any named leg is not on this booking — moving only the legs that resolved is not what
 * the link asked for — or if a moved leg would not stay clear of its neighbours. That is measured
 * against every leg, moving or not, because the calendar is: it holds a leg to the day after the one
 * before it and the day before the one after it, using each leg's new date where it has one and its
 * booked date otherwise. Checking only the named legs would accept a link that moves a return back
 * before an outbound it left untouched.
 */
export function resolveFlightDateChangeDeeplinkLegs(
  requested: Array<FlightDateChangeDeeplinkLeg>,
  legs: Array<App.LegWithFlightItemId>,
): Array<App.LegWithFlightItemIdWithNewDepartureDate> | undefined {
  const newDateByLegId = new Map(requested.map(({ legId, newDepartureDate }) => [legId, newDepartureDate]))

  if (requested.some(({ legId }) => !legs.some(leg => leg.id === legId))) return undefined

  const departureDates = legs.map(
    leg => newDateByLegId.get(leg.id) ?? moment(leg.departureDateTime, DMY_DATETIME_FORMAT).format(ISO_DATE_FORMAT),
  )

  const clearsNeighbours = legs.every((leg, index) => {
    if (!newDateByLegId.has(leg.id)) return true
    const previous = departureDates[index - 1]
    const next = departureDates[index + 1]
    return (previous === undefined || departureDates[index] > previous) &&
      (next === undefined || departureDates[index] < next)
  })

  if (!clearsNeighbours) return undefined

  return nonNullable(legs.map(leg => {
    const newDepartureDate = newDateByLegId.get(leg.id)
    return newDepartureDate ? { ...leg, newDepartureDate } : undefined
  }))
}

/**
 * Whether the order holds nothing but flights, for the purposes of a flight date change.
 *
 * Travel protection is deliberately not counted — it is an attachment to the flight, and the flow's
 * `insurance-select-dates` step realigns the policy with the new dates. This is why it does not reuse
 * `isStandaloneFlightsOrder`, which counts insurance.
 *
 * Derived from `getAllOrderItems` rather than a hand-listed set of item arrays so a newly added item
 * type can't silently pass this gate.
 */
export function isFlightOnlyOrderForDateChange(order: App.Order): boolean {
  if (order.flightItems.length === 0) return false

  // By identity, not a type guard: getAllOrderItems spreads these same object references.
  const nonBlockingItems = new Set<App.AnyOrderItem>([
    ...order.flightItems,
    ...order.bookingProtectionItems,
  ])

  const hasOtherActiveItems = getAllOrderItems(order).some(
    item => !nonBlockingItems.has(item) && !isItemCancelled(item),
  )

  // Gift cards sit outside getAllOrderItems and carry no status.
  return !hasOtherActiveItems && order.giftCardItems.length === 0
}

// A nomination crosses a phone call and several services to get here, so "qf 81" is "QF81" and
// "PREMIUM_ECONOMY" is "Premium Economy". Only case and separators are ignored.
function normaliseForMatching(value: string): string {
  return value.toUpperCase().replace(/[\s_-]+/g, '')
}

// Not defensive on purpose: the segments, carrier code and flight number are all required on the
// contract, and defaulting a missing one to '' would let a multi-segment journey match a nomination for
// fewer segments.
function journeyFlightSignature(journey: App.JourneyV2): string {
  return journey.flightGroup.flights
    .map(flight => normaliseForMatching(`${flight.marketingAirline.code}${flight.flightNumber}`))
    .join(',')
}

function journeyTotalFare(journey: App.JourneyV2): number {
  return journey.price.all.totalFare
}

/** One leg of the itinerary as the search offers it, with what the link agreed for it. */
export interface CombinationLeg {
  legId: string
  /** Every journey the search returned for the leg, before pairing narrows it. */
  journeys: Array<App.JourneyV2>
  /** Set on a leg the customer is moving. The nomination is what the link agreed for it, if it named a flight. */
  changing?: { nomination?: Pick<App.PreselectedFlight, 'flightNumbers' | 'cabin'> }
}

export interface PlacedLeg {
  legId: string
  journey: App.JourneyV2
  changing: boolean
  /** The agreed cabin is not this row's, so unless a fare option supplies it the price they were quoted went with it. */
  cabinUnavailable: boolean
}

export interface PreselectedCombination {
  /** The legs placed, in itinerary order, up to the first moving leg the run could not place. */
  placed: Array<PlacedLeg>
  /** Every moving leg placed. */
  complete: boolean
  /** The moving leg that stopped the run, with the journeys the list will offer it. */
  stoppedAt?: { legId: string, reason: 'flight-not-offered' | 'no-nomination', offered: Array<App.JourneyV2> }
}

/**
 * The journeys a leg lists once the leg before it is taken, derived the way the flight list derives them:
 * the ones sold alongside it, looked up by booking key, or the whole leg when it names none of them.
 */
function journeysSoldWith(
  previous: App.JourneyV2 | undefined,
  journeys: Array<App.JourneyV2>,
): Array<App.JourneyV2> {
  const pairedKeys = previous?.pairedFareId ?? []
  if (!pairedKeys.length) return journeys
  const byBookingKey = new Map(journeys.map(journey => [journey.bookingInfo?.boookingKey, journey]))
  const paired = nonNullable(pairedKeys.map(key => byBookingKey.get(key)))
  return paired.length ? paired : journeys
}

function changingLegsPlaced(combination: PreselectedCombination): Array<PlacedLeg> {
  return combination.placed.filter(leg => leg.changing)
}

// More of the agreed flights, then more of them in the agreed cabin, then cheaper.
function isBetterCombination(candidate: PreselectedCombination, incumbent: PreselectedCombination): boolean {
  const candidateLegs = changingLegsPlaced(candidate)
  const incumbentLegs = changingLegsPlaced(incumbent)
  if (candidateLegs.length !== incumbentLegs.length) return candidateLegs.length > incumbentLegs.length

  const cabinsKept = (legs: Array<PlacedLeg>) => legs.filter(leg => !leg.cabinUnavailable).length
  if (cabinsKept(candidateLegs) !== cabinsKept(incumbentLegs)) return cabinsKept(candidateLegs) > cabinsKept(incumbentLegs)

  const fare = (legs: Array<PlacedLeg>) => legs.reduce((total, leg) => total + journeyTotalFare(leg.journey), 0)
  return fare(candidateLegs) < fare(incumbentLegs)
}

/**
 * Places the whole itinerary on the flights a link agreed to, in one pass.
 *
 * The flight list narrows each leg to what is sold alongside the leg before it, and the same flight
 * comes back as several rows, one per cabin it is sold in, each bound to its own set of later flights.
 * Picking the first leg on its own — even on the cheapest row in the agreed cabin — can therefore leave
 * the agreed return off the list the next leg shows. So every row that carries an agreed flight is tried
 * against the legs after it, and the itinerary that places the most agreed flights wins; among those,
 * the one that keeps the most legs in the agreed cabin, then the cheaper.
 *
 * Matched on the segment sequence rather than a fare id: the list runs its own search, so ids from
 * anywhere else do not exist in its results, but flight numbers survive a re-search. Prices are not
 * matched — they may have moved since the call, and the customer sees the amount again before paying.
 * The cabin is only preferred: Business on a flight is often sold as a fare option on the Economy row
 * rather than as a row of its own, which {@link resolveAgreedUpsellFares} settles once the rows are
 * known. A leg that is not moving takes what the list itself would put it on.
 */
export function matchPreselectedCombination(legs: Array<CombinationLeg>): PreselectedCombination {
  let best: PreselectedCombination | undefined
  const consider = (candidate: PreselectedCombination) => {
    if (!best || isBetterCombination(candidate, best)) best = candidate
  }

  const visit = (position: number, previous: App.JourneyV2 | undefined, placed: Array<PlacedLeg>) => {
    if (position === legs.length) {
      consider({ placed, complete: true })
      return
    }

    const leg = legs[position]
    const offered = journeysSoldWith(previous, leg.journeys)

    if (!leg.changing) {
      const journey = offered[0]
      visit(position + 1, journey, journey ? [...placed, { legId: leg.legId, journey, changing: false, cabinUnavailable: false }] : placed)
      return
    }

    const nomination = leg.changing.nomination
    if (!nomination?.flightNumbers.length) {
      consider({ placed, complete: false, stoppedAt: { legId: leg.legId, reason: 'no-nomination', offered } })
      return
    }

    const wanted = nomination.flightNumbers.map(normaliseForMatching).join(',')
    const sameItinerary = offered.filter(journey => journeyFlightSignature(journey) === wanted)
    if (!sameItinerary.length) {
      consider({ placed, complete: false, stoppedAt: { legId: leg.legId, reason: 'flight-not-offered', offered } })
      return
    }

    const wantedCabin = nomination.cabin ? normaliseForMatching(nomination.cabin) : undefined
    for (const journey of sameItinerary) {
      const cabinUnavailable = !!wantedCabin && normaliseForMatching(journey.cabin) !== wantedCabin
      visit(position + 1, journey, [...placed, { legId: leg.legId, journey, changing: true, cabinUnavailable }])
    }
  }

  visit(0, undefined, [])

  return best ?? { placed: [], complete: true }
}

/** What a leg's list offers, compactly, for the log line written when a nomination cannot be honoured. */
export function summariseOfferedJourneys(journeys: Array<App.JourneyV2>): Array<string> {
  return journeys.slice(0, 20).map(journey => `${journeyFlightSignature(journey)} ${journey.cabin ?? ''}`.trim())
}

/** Whether a fare option puts the customer in the cabin the link named. */
export function fareFamilySuppliesCabin(fare: App.FlightFareFamily | undefined, cabin: string | undefined): boolean {
  return !!fare?.cabin && !!cabin && normaliseForMatching(fare.cabin) === normaliseForMatching(cabin)
}

/**
 * Finds the fare option the customer agreed to among the ones sold on a leg.
 *
 * The list is the leg's upsell fares, which is where the exchange search puts every option including the
 * one the journey already carries — a journey only ever names the cheapest of them, so an agreed Flex is
 * invisible at the journey level. An agent quotes it by name ("Flex") but the carrier's code ("FL") also
 * travels on the link, so either identifies it.
 *
 * The same name is sold in more than one cabin, Flex in Economy and Flex in Business, so where the link
 * names a cabin the option has to be in it too: a namesake in another cabin is not what they were quoted.
 * An option the search gives no cabin for is taken on its name alone.
 */
export function matchUpsellFare(
  upsellFares: Array<App.FlightFareFamily>,
  fare: string | undefined,
  cabin?: string,
): App.FlightFareFamily | undefined {
  if (!fare) return undefined
  const wanted = normaliseForMatching(fare)
  const byName = upsellFares.filter(({ fareFamily }) =>
    normaliseForMatching(fareFamily.name) === wanted ||
    normaliseForMatching(fareFamily.description) === wanted,
  )
  if (!cabin) return byName[0]
  return byName.find(option => fareFamilySuppliesCabin(option, cabin)) ?? byName.find(option => !option.cabin)
}

export interface AgreedUpsellFare {
  /** Undefined where the search offered this leg no option it could take. */
  fareFamily: App.FlightFareFamily | undefined
  /** A fare was agreed for this leg and is not sold alongside what the rest of the itinerary takes. */
  agreedFareGone: boolean
}

function upsellFareCost(fare: App.FlightFareFamily): number {
  return fare.costWithInsurance?.all ?? fare.cost.all ?? 0
}

/** The options on a leg that are sold alongside the one an earlier leg has already taken. */
function sellableAlongside(
  offered: Array<App.FlightFareFamily>,
  taken: App.FlightFareFamily | undefined,
): Array<App.FlightFareFamily> {
  if (!taken) return EmptyArray
  return offered.filter(fare => fare.pairedUpsellIds?.includes(taken.id))
}

/** What the link agreed for a leg's fare: the option by name, and the cabin it was to be in. */
export interface AgreedFareOption {
  fare?: string
  cabin?: string
}

function cheapestFareInCabin(fares: Array<App.FlightFareFamily>, cabin: string | undefined): App.FlightFareFamily | undefined {
  return min(fares.filter(fare => fareFamilySuppliesCabin(fare, cabin)), upsellFareCost)
}

/**
 * Resolves the fare option each leg of the itinerary ends up on, given what was agreed for each.
 *
 * Indexed by position, the order the exchange search returns fare options in: the departing leg's
 * options first, then the returning ones, each of which is only sold alongside particular departing
 * options (`pairedUpsellIds`). Legs are resolved in that order rather than each on its own name,
 * because two independently-matched "Flex" options can be a pair the carrier does not sell together —
 * the same order and the same paired filter the fare family modal applies when the customer picks by
 * hand.
 *
 * Where the agreed option has gone, or none was agreed for that leg, the cheapest of what is still sold
 * stands in: that is the option the journey already carries, so the price does not move. A link that
 * named a cabin but no option takes the cheapest option in that cabin instead: on a row sold in another
 * cabin, that option is the only thing that delivers what was agreed. A leg the search offers nothing
 * for takes nothing, which is how the manual path books it too.
 */
export function resolveAgreedUpsellFares(
  upsellFaresPerLeg: Array<Array<App.FlightFareFamily>>,
  agreedPerLeg: Array<AgreedFareOption | undefined>,
): Array<AgreedUpsellFare> {
  // Nothing was agreed, so there is nothing to hold the itinerary to: it takes no fare options, which is
  // how the manual path books a combination the search offers none for.
  if (!agreedPerLeg.some(agreed => agreed?.fare || agreed?.cabin)) return EmptyArray

  const resolved: Array<AgreedUpsellFare> = []

  for (const [position, offered] of upsellFaresPerLeg.entries()) {
    // Past the first leg, only what is sold alongside the option already taken is on offer.
    const sellable = position === 0 ? offered : sellableAlongside(offered, resolved[position - 1].fareFamily)

    const agreed = agreedPerLeg[position]
    const matched = matchUpsellFare(sellable, agreed?.fare, agreed?.cabin)

    resolved.push({
      fareFamily: matched ?? cheapestFareInCabin(sellable, agreed?.cabin) ?? min(sellable, upsellFareCost),
      // Only where the leg sells options at all: an empty list means the search said nothing about this
      // leg's fares, which is not the same as the agreed one having sold out.
      agreedFareGone: !!agreed?.fare && !matched && !!offered.length,
    })
  }

  return resolved
}
